import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiFileText } from "react-icons/fi";
import { IoIosRemoveCircleOutline, IoMdCheckmarkCircleOutline } from "react-icons/io";
import { LuBookMarked, LuChartColumn, LuDatabase, LuFactory, LuFileDown, LuFlaskConical, LuFolderOpen, LuImage, LuLeaf, LuLock, LuLockOpen, LuNewspaper, LuPlus, LuQrCode, LuRecycle, LuShield, LuWrench } from "react-icons/lu";
import { MdOutlineRadioButtonUnchecked } from "react-icons/md";
import { RiDownloadCloud2Line, RiFileUploadLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import foot from "../../../assets/logo/foot.png";
import { fetchWithToken, useAuth } from "../../../authentication/AuthProvider";
import RefreshButton from "../../../components/button/RefreshButton";
import { MainContainer } from "../../../components/container/MainContainer";
import { TableHeaderContainer } from "../../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../../components/container/TableTitleContainer";

import CheckBox from "../../../components/input/CheckBox";
import ImageUpload from "../../../components/input/ImageUpload";
import TextBox from "../../../components/input/TextBox";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import SegmentedTabs from "../../../components/tabs/SegmentedTabs";
import config from "../../../config";
import { DPP } from "../../../db_access/dpp";
import { EnvFactor } from "../../../db_access/env_factor";
import { Product } from "../../../db_access/product";
import EvidenceRepository from "../../../organizational-page-components/EvidenceRepository";
import routing from "../../../routing";
import { importWorkspacePhotoToEvidence } from "./importWorkspacePhoto";
import MaterialCompositionGrid from "./MaterialCompositionGrid";
import VisitLogGrid from "./VisitLogGrid";
import ProductDPP_CreateForm from "./ProductDPP_CreateForm";
import { dedupeMaterials } from "./dedupeMaterials";
import { dpp_format } from "./ProductDPP_Format";
import ProductDPP_Preview from "./ProductDPP_Preview";

// A list item that still matches its format's blank template exactly — i.e.
// the placeholder row every list field starts with — rather than something
// the user (or a prior load) actually filled in. Compares against the
// format's actual default[0] template object (not itemDef's per-field
// defaults, which can drift out of sync with it) so it stays correct
// regardless of any such mismatch.
const isBlankListItem = (item, template) => !!template && Object.keys(template).every((key) => (item?.[key] ?? template[key]) === template[key]);
const ProductDPP_Edit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedProducts, enqueueAlert } = useContextProvider();
  const fileInputRef = useRef(null);
  const create = { label: "Create", value: "Create" };
  const preview = { label: "Preview", value: "Preview" };
  const publishTab = { label: "Publish", value: "Publish" };
  const evidence = { label: "Evidence Repository", value: "Evidence Repository" };
  const tabs = [create, preview, publishTab, evidence];
  const [activeTab, setActiveTab] = useState(create.value);
  useEffect(() => {
    if (selectedProducts.length !== 1) {
      navigate(routing.sector.products.dashboard);
    }
  }, [selectedProducts, navigate]);
  const [dpp, setDPP] = useState(
    dpp_format.reduce((acc, item) => {
      acc[item.field] = item.default;
      return acc;
    }, {})
  );
  const [sectionEditability, setSectionEditability] = useState(
    [...new Set(dpp_format.map((item) => item.section))].reduce((acc, section) => {
      acc[section] = true;
      return acc;
    }, {})
  );
  const isFieldEditable = (field) => {
    const format = dpp_format.find((f) => f.field === field);
    return format ? sectionEditability[format.section] ?? true : true;
  };
  const [photo, setPhoto] = useState();
  const [photoPreview, setPhotoPreview] = useState();
  const [photoRefreshSignal, setPhotoRefreshSignal] = useState(0);

  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const { user } = useAuth();
  const isCAdmin = user.is_company_admin === 1;
  const buttonCustomize = (label, Icon, onClick) => (
    <Button
      onClick={onClick}
      sx={{
        color: "#166534",
        textTransform: "none",
        "&:hover": { backgroundColor: "rgba(23, 163, 74, 0.2)" },
        fontFamily: "Poppins",
      }}
      startIcon={<Icon />}>
      {t(label)}
    </Button>
  );
  const savePhoto = (dpp_id) => {
    const fd = new FormData();
    fd.append("id", dpp_id);
    fd.append("photo", photo);
    DPP.save_photo(fd);
  };
  const [isPublishing, setIsPublishing] = useState(false);
  const publish = () => {
    if (isPublishing) return;
    setIsPublishing(true);
    DPP.new_published(dpp)
      .then((res) => res.json())
      .then((data) => {
        if (data["dpp_id"]) {
          const dpp_id = data["dpp_id"];
          setDPP((i) => ({ ...i, _id: dpp_id }));
          if (selectedProducts.length === 1) {
            savePhoto(dpp_id);
          }
        }
      })
      .finally(() => setIsPublishing(false));
  };
  const buttonPublish = () => buttonCustomize(isPublishing ? (dpp._id ? "Republishing..." : "Publishing...") : dpp._id ? "Republish" : "Publish", LuNewspaper, publish);
  const pending = () => {
    DPP.new_pending({ ...dpp, id: selectedProducts[0]["id"] })
      .then((res) => res.json())
      .then((data) => {
        if (data["dpp_id"]) {
          const dpp_id = data["dpp_id"];
          setDPP((i) => ({ ...i, _id: dpp_id }));
          if (selectedProducts.length === 1) {
            savePhoto(dpp_id);
          }
        }
      });
  };
  const buttonPending = () => buttonCustomize("Pending", LuNewspaper, pending);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    if (isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      const { generateDppPdf } = await import("./generateDppPdf");
      await generateDppPdf(`${dpp.product_name || "digital-product-passport"}.pdf`);
    } finally {
      setIsDownloadingPdf(false);
    }
  };
  const buttonDownloadPdf = () => buttonCustomize(isDownloadingPdf ? "Generating..." : "Download PDF", LuFileDown, handleDownloadPdf);
  const loadPhotoFromArivu = () => {
    // const url = `${config.product.get_photo}?id=${selectedProducts[0]["id"]}`;
    return Product.get_photo(dpp["id"])
      .then((res) => {
        if (res.ok) {
          return res.blob();
        } else {
          return undefined;
        }
      })
      .then(async (data) => {
        setPhoto(data);
        if (data && selectedProducts[0]?.id) {
          await importWorkspacePhotoToEvidence(data, selectedProducts[0]["id"], "product_id");
          setPhotoRefreshSignal((s) => s + 1);
        }
      });
  };
  const loadMaterialsFromArivu = () => {
    return EnvFactor.get_materials_by_product_id(selectedProducts[0]["id"])
      .then((res) => res.json())
      .then((data) => {
        const materials = data.map((d) => ({
          type: d.type,
          category: d.category,
          description: d.description,
          quantity: d.quantity,
          unit: d.unit,
          gwp: d.gwp_total,
          biogenic_gwp: d.biogenic_gwp,
          _source: "arivu",
        }));
        const materialsTemplate = dpp_format.find((f) => f.field === "materials").default[0];
        setDPP((prev) => ({
          ...prev,
          // Drop any rows from a previous "Load from Arivu" click (tagged _source)
          // before appending the fresh fetch, so re-clicking replaces rather than
          // duplicating. Rows the user added manually (no _source tag) are kept.
          materials: [
            ...prev.materials.filter((m) => !isBlankListItem(m, materialsTemplate) && m._source !== "arivu"),
            ...materials,
          ],
        }));
      });
  };
  const [isLoadingArivu, setIsLoadingArivu] = useState(false);
  const loadFromArivu = () => {
    if (isLoadingArivu) return;
    setIsLoadingArivu(true);
    const mainLoad = Product.get_by_id(selectedProducts[0]["id"])
      .then((res) => res.json())
      .then((data) =>
        setDPP((prev) => {
          const product = data[0];
          const passport = {
            product_id: product.id,
            mci: product.mci,
            gwp_total: product.gwp,
            manufacturer_name: product.supplier,
            manufacturer_address: product.detail_address,
            manufacturer_email: product.detail_email,
            manufacturer_phone: product.detail_phone,
            manufacturer_contact_person: product.detail_contact_person,
            service_life: product.service_life,
            extended_service_life: product.extended_service_life,
            leed_category: product.leed_category,
            sub_category: product.sub_category,
            uniformat_II_1: product.uniformat_II_1,
            uniformat_II_2: product.uniformat_II_2,
            place_of_manufacture: product.place_of_manufacture,
            place_of_use: product.place_of_use,
            product_name: product.name,
          };
          const updated = {};
          const appended = {};

          for (const key in passport) {
            if (isFieldEditable(key)) {
              updated[key] = passport[key];
            } else {
              const format = dpp_format.find((f) => f.field === key);
              if (format && format.type === "list") {
                appended[key] = passport[key];
              }
            }
          }
          for (const key in appended) {
            const prevList = Array.isArray(prev[key]) ? prev[key] : [];
            const template = dpp_format.find((f) => f.field === key)?.default?.[0];
            const cleanedPrevList = prevList.filter((item) => !isBlankListItem(item, template));
            updated[key] = [...cleanedPrevList, ...appended[key]];
          }
          return {
            ...prev,
            ...updated,
          };
        })
      );
    Promise.all([mainLoad, loadPhotoFromArivu(), loadMaterialsFromArivu()])
      .then(() => enqueueAlert({ message: "Data Loaded Successfully", severity: "success" }))
      .finally(() => setIsLoadingArivu(false));
  };
  const buttonLoadFromArivu = () => buttonCustomize(isLoadingArivu ? "Loading..." : "Load from Arivu", RiDownloadCloud2Line, loadFromArivu);

  const loadFromPDF = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const buttonLoadFromPDF = () => buttonCustomize(isLoadingPdf ? "Loading..." : "Load from PDF", RiFileUploadLine, loadFromPDF);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file || file.type !== "application/pdf") {
      return;
    }
    setIsLoadingPdf(true);
    const url = `${config.apiUrl}/api/dpp/pdf_to_passport_json_endpoint`;
    const body = new FormData();
    body.append("file", file);
    fetchWithToken(url, {
      ...config.method.POST,
      body,
    })
      .then((res) => res.json())
      .then((data) => {
        const passport = data["data"];
        const materials_json = passport["materials_json"];
        const materials = typeof materials_json === "string" ? JSON.parse(materials_json) : materials_json;
        passport["materials"] = materials;
        const certifications_json = passport["certifications_json"];
        const certifications = typeof certifications_json === "string" ? JSON.parse(certifications_json) : certifications_json;
        passport["certifications"] = certifications;
        setDPP((prev) => {
          const updated = {};
          const appended = {};

          for (const key in passport) {
            if (isFieldEditable(key)) {
              updated[key] = passport[key];
            } else {
              const format = dpp_format.find((f) => f.field === key);
              if (format && format.type === "list") {
                appended[key] = passport[key];
              }
            }
          }
          for (const key in appended) {
            const prevList = Array.isArray(prev[key]) ? prev[key] : [];
            const template = dpp_format.find((f) => f.field === key)?.default?.[0];
            const cleanedPrevList = prevList.filter((item) => !isBlankListItem(item, template));
            updated[key] = [...cleanedPrevList, ...appended[key]];
          }
          return {
            ...prev,
            ...updated,
          };
        });
        enqueueAlert({ message: "Data Loaded Successfully", severity: "success" });
      })
      .finally(() => setIsLoadingPdf(false));
  };
  const onChange = (e, field, index = undefined, subField = undefined) => {
    const attribute = dpp_format.find((d) => d.field === field);
    if (["string"].includes(attribute.type)) {
      setDPP((p) => ({ ...p, [field]: e.target.value }));
    } else if (["float"].includes(attribute.type)) {
      setDPP((p) => ({ ...p, [field]: parseFloat(e.target.value) }));
    } else if (["boolean"].includes(attribute.type)) {
      setDPP((p) => ({ ...p, [field]: e.target.checked }));
    } else if (["list"].includes(attribute.type)) {
      const subAttribute = attribute.itemDef.find((d) => d.field === subField);
      setDPP((p) => ({
        ...p,
        [field]: p[field].map((item, i) => {
          if (i === index) {
            if (["string", "float"].includes(subAttribute.type)) {
              return { ...item, [subField]: e.target.value };
            } else if (["boolean"].includes(subAttribute.type)) {
              return { ...item, [subField]: e.target.checked };
            }
          }
          return item;
        }),
      }));
    }
  };
  const buttonLock = (visible, onClick) => {
    return (
      <Box sx={{ display: visible ? "block" : "none" }}>
        <IconButton
          onClick={onClick}
          sx={{
            "&:hover": { backgroundColor: "transparent" },
            padding: 1,
            color: "black",
          }}>
          <LuLock />
        </IconButton>
      </Box>
    );
  };
  const buttonUnlock = (visible, onClick) => {
    return (
      <Box sx={{ display: visible ? "block" : "none" }}>
        <IconButton
          onClick={onClick}
          sx={{
            "&:hover": { backgroundColor: "transparent", color: "black" },
            padding: 1,
            color: "lightgray",
          }}>
          <LuLockOpen />
        </IconButton>
      </Box>
    );
  };
  const buttonSectionLockUnlock = (section) => {
    const editable = sectionEditability[section] ?? true;
    const setEditable = (value) => {
      setSectionEditability((prev) => ({ ...prev, [section]: value }));
    };
    return (
      <Box>
        {buttonLock(!editable, () => setEditable(true))}
        {buttonUnlock(editable, () => setEditable(false))}
      </Box>
    );
  };
  const dpp_control = (section) => {
    const editable = sectionEditability[section] ?? true;
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {dpp_format
          .filter((d) => d.section === section)
          .map((d, i) => {
            if (["string", "float"].includes(d.type)) {
              return (
                <Box
                  key={i}
                  sx={{
                    minWidth: "300px",
                    display: "flex",
                    alignItems: "flex-end",
                  }}>
                  <TextBox label={d.label} value={dpp[d.field]} onChange={(e) => onChange(e, d.field)} disabled={d.disabled || !editable} placeholder={d.label} />
                </Box>
              );
            }
            if (["boolean"].includes(d.type)) {
              return (
                <Box
                  key={i}
                  sx={{
                    minWidth: "300px",
                    display: "flex",
                    alignItems: "flex-end",
                  }}>
                  <CheckBox
                    label={d.label}
                    checked={dpp[d.field]}
                    onChange={(e) => onChange(e, d.field)}
                    Icon={MdOutlineRadioButtonUnchecked}
                    CheckIcon={IoMdCheckmarkCircleOutline}
                    disabled={!editable}
                  />
                </Box>
              );
            }
            if (d.field === "materials") {
              return (
                <MaterialCompositionGrid
                  key={i}
                  label={d.label}
                  itemDef={d.itemDef}
                  items={dpp[d.field]}
                  editable={editable}
                  onChange={(newItems) => setDPP((p) => ({ ...p, [d.field]: newItems }))}
                />
              );
            }
            if (["list"].includes(d.type)) {
              const newItem = () => {
                const defaultItem = d.itemDef.reduce((acc, def) => {
                  acc[def.field] = def.default;
                  return acc;
                }, {});
                setDPP((p) => ({
                  ...p,
                  [d.field]: [...p[d.field], defaultItem],
                }));
              };
              const itemDef = d.itemDef;
              return (
                <Box key={i} sx={{ width: 1 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {dpp[d.field].map((m, mi) => (
                      <Box
                        key={mi}
                        sx={{
                          display: "flex",
                        }}>
                        <IconButton
                          disabled={!editable}
                          sx={{
                            marginTop: 2.5,
                            marginBottom: "auto",
                            color: "red",
                          }}
                          onClick={() => {
                            setDPP((p) => ({
                              ...p,
                              [d.field]: p[d.field].filter((item, index) => index !== mi),
                            }));
                          }}>
                          <IoIosRemoveCircleOutline />
                        </IconButton>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}>
                          {itemDef.map((def, defindex) => {
                            if (def.computed) {
                              const totalQty = dpp[d.field].reduce((s, item) => s + (parseFloat(item.quantity) || 0), 0);
                              const qty = parseFloat(m.quantity) || 0;
                              const pct = totalQty > 0 ? (qty / totalQty * 100).toFixed(1) : "0.0";
                              return (
                                <Box key={defindex} sx={{ minWidth: "260px" }}>
                                  <TextBox label={def.label} value={pct} onChange={() => {}} placeholder={def.label} />
                                </Box>
                              );
                            }
                            if (["string", "float"].includes(def.type)) {
                              return (
                                <Box key={defindex} sx={{ minWidth: "260px" }}>
                                  <TextBox label={def.label} value={m[def.field]} onChange={(e) => onChange(e, d.field, mi, def.field)} disabled={!editable} placeholder={def.label} />
                                </Box>
                              );
                            }
                            if (["boolean"].includes(def.type)) {
                              return (
                                <Box
                                  key={defindex}
                                  sx={{
                                    minWidth: "300px",
                                    alignItems: "flex-end",
                                    display: "flex",
                                  }}>
                                  <CheckBox
                                    label={def.label}
                                    checked={m[def.field]}
                                    onChange={(e) => onChange(e, d.field, mi, def.field)}
                                    Icon={MdOutlineRadioButtonUnchecked}
                                    CheckIcon={IoMdCheckmarkCircleOutline}
                                    disabled={!editable}
                                  />
                                </Box>
                              );
                            }
                            return null;
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ paddingY: 1.5 }} />
                  <Box sx={{ display: "flex", opacity: editable ? 1 : 0.5, pointerEvents: editable ? "auto" : "none" }}>
                    {buttonCustomize(`${t("Add")} ${t(d.label)}`, LuPlus, newItem)}
                  </Box>
                </Box>
              );
            }
            return null;
          })}
      </Box>
    );
  };
  const section_title = (title, Icon, iconColor) => {
    return (
      <TableHeaderContainer>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: 1,
          }}>
          <Icon color={iconColor} size={20} />
          <Typography
            sx={{
              color: "black",
              fontFamily: "Poppins",
              fontSize: "22px",
              fontWeight: 500,
            }}>
            {t(title)}
          </Typography>
        </Box>
        <Box>{buttonSectionLockUnlock(title)}</Box>
      </TableHeaderContainer>
    );
  };
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
    } else {
      setPhoto(undefined);
    }
  };
  const getPassport = () => {
    DPP.get_published(selectedProducts[0]["id"])
      .then((res) => res.json())
      .then((data) => {
        if (data.length !== 0) {
          const passport = data[0];
          setDPP((i) => ({ ...i, ...passport, materials: dedupeMaterials(passport.materials) }));
        }
      });
  };
  const getPhoto = () => {
    DPP.get_photo(dpp._id)
      .then((res) => (res.ok ? res.blob() : undefined))
      .then((f) => setPhoto(f));
  };
  useEffect(() => {
    if (selectedProducts.length > 0) {
      const p = selectedProducts[0];
      const temp = { ...dpp, product_name: p?.name, id: p?.id };
      setDPP(temp);
    }
  }, [selectedProducts]);
  useEffect(() => {
    if (selectedProducts.length !== 1) {
      return;
    }
    getPassport();
  }, [selectedProducts]);
  useEffect(() => {
    if (dpp._id) {
      getPhoto();
    }
  }, [dpp._id]);
  useEffect(() => {
    if (photo) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(photo);
    } else {
      setPhotoPreview(undefined);
    }
  }, [photo]);
  return (
    <MainContainer>
      <Box sx={{ display: selectedProducts.length === 1 ? "block" : "none" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
          <Box sx={{ width: "100%", maxWidth: "800px" }}>
            <SegmentedTabs options={tabs} selected={activeTab} setSelected={setActiveTab} />
          </Box>
        </Box>

        <Box sx={{ display: activeTab === create.value ? "block" : "none" }}>
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5 }}>
            {buttonLoadFromArivu()}
            {buttonLoadFromPDF()}
          </Box>
          <ProductDPP_CreateForm
            dpp={dpp}
            dppControl={dpp_control}
            entityId={selectedProducts[0]?.id}
            qrValue={`${baseUrl}${routing.digital_passport.view_published}/${dpp.id}`}
            photoRefreshSignal={photoRefreshSignal}
            onPhotosChanged={() => setPhotoRefreshSignal((s) => s + 1)}
          />
          <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileChange} style={{ display: "none" }} />
          {/*
          ── Old Create tab content (stacked-cards layout), preserved for reference ──
          <Box sx={{ display: "flex", gap: 2, paddingY: 2 }}>
            <Box sx={{ flex: 3, display: "flex", flexDirection: "column", gap: 2 }}>
              <Paper sx={{ padding: 2 }}>
                {section_title("Product Identity", FiFileText, "#2cbea5")}
                {dpp_control("Product Identity")}
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("Environmental Impact", LuLeaf, "#2cbea5")}
                {dpp_control("Environmental Impact")}
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("Material Composition", LuFactory, "#2cbea5")}
                {dpp_control("Material Composition")}
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("Certificates", FiFileText, "#2cbea5")}
                {dpp_control("Certificates")}
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("Durability, Repair & Circular Design", LuWrench, "#2cbea5")}
                {dpp_control("Durability, Repair & Circular Design")}
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("Functional Performance & Design", LuChartColumn, "#2cbea5")}
                {dpp_control("Functional Performance & Design")}
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("Substances of Concern", LuFlaskConical, "#2cbea5")}
                {dpp_control("Substances of Concern")}
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("Producer & Facility Identification", LuFolderOpen, "#2cbea5")}
                {dpp_control("Producer & Facility Identification")}
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("Compliance & Other Union Law", LuBookMarked, "#2cbea5")}
                {dpp_control("Compliance & Other Union Law")}
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("DPP System — Access, Governance & Registry", LuDatabase, "#2cbea5")}
                {dpp_control("DPP System — Access, Governance & Registry")}
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("Data Carrier", LuQrCode, "#2cbea5")}
                {dpp_control("Data Carrier")}
              </Paper>
            </Box>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
              <Paper sx={{ padding: 2 }}>
                <TableHeaderContainer>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, padding: 1 }}>
                    <LuImage color={"#2cbea5"} size={20} />
                    <Typography sx={{ color: "black", fontFamily: "Poppins", fontSize: "22px", fontWeight: 500 }}>{t("Photo")}</Typography>
                  </Box>
                </TableHeaderContainer>
                <ImageUpload placeholder={"Click to upload photo"} onChange={handlePhotoChange} preview={photoPreview} accept={"image/*"} deletePreview={() => setPhoto(undefined)} />
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("Compliance", LuShield, "#2cbea5")}
                {dpp_control("Compliance")}
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("Circularity", LuRecycle, "#2cbea5")}
                {dpp_control("Circularity")}
              </Paper>
              <Paper sx={{ padding: 2 }}>
                {section_title("Hosting", LuFactory, "#2cbea5")}
                {dpp_control("Hosting")}
                <Box sx={{ display: dpp._id ? "block" : "none" }}>
                  <Box>
                    <QRCodeCanvas
                      value={`${baseUrl}${routing.digital_passport.view_published}/${dpp.id}`}
                      size={256}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      level="H"
                      marginSize={10}
                      imageSettings={{
                        src: foot,
                        x: undefined,
                        y: undefined,
                        height: 24,
                        width: 24,
                        opacity: 1,
                        excavate: true,
                      }}
                    />
                  </Box>
                  <Box sx={{ "&:hover": { cursor: "pointer" } }} onClick={() => window.open(hostingUrl, "_blank")}>
                    <Typography> {hostingUrl} </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
          */}
        </Box>

        <Box sx={{ display: activeTab === preview.value ? "block" : "none", gap: 2, paddingY: 2 }}>
          <ProductDPP_Preview dpp={dpp} photoPreview={photoPreview} entityId={selectedProducts[0]?.id} photoRefreshSignal={photoRefreshSignal} />
        </Box>

        <Box sx={{ display: activeTab === publishTab.value ? "block" : "none", gap: 2, paddingY: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
            {buttonPublish()}
            {buttonPending()}
            {dpp._id && buttonDownloadPdf()}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "stretch" }}>
          <Paper sx={{ padding: 3, flex: 1, minWidth: 320, maxWidth: 500 }}>
            {section_title("Hosting", LuFactory, "#2cbea5")}
            {dpp_control("Hosting")}
          </Paper>
          <Paper sx={{ padding: 3, flex: 1, minWidth: 320, maxWidth: 500, display: "flex", flexDirection: "column" }}>
            {section_title("QR Code", LuQrCode, "#2cbea5")}
            {dpp._id ? (
              <Box sx={{ mt: 2, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "18px", fontFamily: "Poppins", textAlign: "center" }}>{dpp.product_name}</Typography>
                <QRCodeCanvas
                  value={`${baseUrl}${routing.digital_passport.view_published}/${selectedProducts[0]?.id}`}
                  size={256} // Size in pixels
                  bgColor="#ffffff" // Background color
                  fgColor="#000000" // Foreground color
                  level="H" // Error correction level: 'L', 'M', 'Q', 'H'
                  marginSize={10}
                  imageSettings={{
                    src: foot,
                    x: undefined,
                    y: undefined,
                    height: 24,
                    width: 24,
                    opacity: 1,
                    excavate: true,
                  }}
                />
                {/*
                Hidden now that Download PDF covers this — preserved for reference.
                <Box sx={{ "&:hover": { cursor: "pointer" }, color: "#166534", textDecoration: "underline" }} onClick={() => window.open(hostingUrl, "_blank")}>
                  <Typography>{hostingUrl}</Typography>
                </Box>
                */}
              </Box>
            ) : (
              <Typography sx={{ mt: 2, color: "#94a3b8", fontFamily: "Poppins" }}>
                {t("Publish or mark this DPP as Pending to generate a QR code.")}
              </Typography>
            )}
          </Paper>
          </Box>
          {dpp._id && (
            <Paper variant="Table Container" sx={{ mt: 2 }}>
              <TableHeaderContainer>
                <TableTitleContainer>
                  <Typography variant="Table Title">{t("QR Scan Activity")}</Typography>
                </TableTitleContainer>
                <TableHeaderToolsContainer>
                  <RefreshButton />
                </TableHeaderToolsContainer>
              </TableHeaderContainer>
              <VisitLogGrid entityId={selectedProducts[0]?.id} entityType="product" />
            </Paper>
          )}
          {dpp._id && (
            <Box sx={{ position: "absolute", left: "-9999px", top: 0, width: "800px" }}>
              <ProductDPP_Preview
                dpp={dpp}
                entityId={selectedProducts[0]?.id}
                fullMode
                qrValue={`${baseUrl}${routing.digital_passport.view_published}/${selectedProducts[0]?.id}`}
                photoRefreshSignal={photoRefreshSignal}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ display: activeTab === evidence.value ? "block" : "none", gap: 2, paddingY: 2 }}>
          <EvidenceRepository
            entityId={selectedProducts[0]?.id}
            idColumnName="product_id"
            scope="DPP Evidence"
            extraRows={(dpp.certifications || [])
              .filter((c) => c.url)
              .map((c, i) => ({
                id: `cert-${i}`,
                name: c.name || `${t("Certificate")} ${i + 1}`,
                url: c.url,
                uploaded_at: c.issueDate,
              }))}
          />
        </Box>
      </Box>
    </MainContainer>
  );
};
export default ProductDPP_Edit;
